import type { ISODateTimeString } from '@gat-ship/types'
import { Temporal } from '@js-temporal/polyfill'
import { computed, toValue, type MaybeRefOrGetter, type Ref } from 'vue'
import { getCurrentTimeZoneId } from './date-utils'
import type { DateInputCalendarModelValue } from '../../gat-date-calendar/gat-date-calendar.vue'
import { useDateInputModel } from './useModelValue'
import type { GatDateEditProps } from '../gat-date-edit.vue'

export function useDatePicker(
  props: {
    modelValue: MaybeRefOrGetter<GatDateEditProps['modelValue']>
    timeZone: MaybeRefOrGetter<GatDateEditProps['timeZone']>
    anchorTime: MaybeRefOrGetter<GatDateEditProps['anchorTime']>
  },
  updateModel: ReturnType<typeof useDateInputModel>['updateModel']
) {
  const modelValue = computed(() => toValue(props.modelValue))
  const timeZone = computed(() => toValue(props.timeZone) ?? getCurrentTimeZoneId())
  const anchorTime = computed(() => toValue(props.anchorTime))

  /** The currently selected date. */
  const selectedDate = computed(() => {
    if (!modelValue.value) {
      return undefined
    }
    // Safely parse the model value by first creating an Instant, then converting to ZonedDateTime
    const instant = Temporal.Instant.from(modelValue.value)
    const zonedDateTime = instant.toZonedDateTimeISO(timeZone.value)
    return zonedDateTime.toPlainDate()
  })

  /** The label of the date-picker. */
  const datePickerLabel = computed(() => {
    if (!modelValue.value) {
      return 'Pick a date'
    }
    return Temporal.Instant.from(modelValue.value).toLocaleString(
      Intl.DateTimeFormat().resolvedOptions().locale,
      {
        dateStyle: 'long',
        // hour: props.type === 'datetime' ? '2-digit' : undefined,
        // minute: props.type === 'datetime' ? '2-digit' : undefined,
        hour12: false
      }
    )
  })

  function handleDateChange(date: DateInputCalendarModelValue) {
    if (!date) return

    // Since we're using mode="single", date should be a PlainDate
    // But we need to handle the type safety
    if ('from' in date) {
      // This is a DateRange, but we're in single mode, so this shouldn't happen
      console.warn('Received DateRange in single mode calendar')
      return
    }

    // date is now guaranteed to be a Temporal.PlainDate
    const plainDate = date as Temporal.PlainDate

    const currentModel = modelValue.value
      ? Temporal.Instant.from(modelValue.value).toZonedDateTimeISO(timeZone.value)
      : undefined

    // Convert the date to a zoned datetime in the current timezone, then to an instant
    const zonedDateTime = Temporal.ZonedDateTime.from({
      year: plainDate.year,
      month: plainDate.month,
      day: plainDate.day,
      hour: currentModel?.hour,
      minute: currentModel?.minute,
      second: currentModel?.second,
      millisecond: currentModel?.millisecond,
      microsecond: currentModel?.microsecond,
      nanosecond: currentModel?.nanosecond,
      timeZone: timeZone.value
    })

    updateModel(zonedDateTime.toInstant().toString() as ISODateTimeString, {
      timeZone: timeZone.value,
      anchorTime: anchorTime.value
    })
    // model.value = zonedDateTime.toInstant().toString() as ISODateTimeString
  }

  return {
    selectedDate,
    datePickerLabel,
    handleDateChange
  }
}
